import EventEmitter from "events";
import { isDevelopmentEnvironment } from "../estada-config";

export const WssIonosUrl = "wss://backendapp.estada.de"; // no itx
export const WssAzureUrl = "wss://estada.azurewebsites.net"; // no itx

export let etsEventEmitter = new EventEmitter();
export let syncProgress: boolean = false;
export let etsPin: string = "";
export let selectedLocalEtsProjectId: string = "";

export function setSyncProgress(status: boolean) {
  syncProgress = status;
  etsEventEmitter.emit("syncProgressChange", syncProgress); // no itx
}

export function setSelectedLocalEtsProjectId(projectId: string) {
  selectedLocalEtsProjectId = projectId || "";
}

export class ETSWebSocket {
  private socket: WebSocket | null = null;
  private readonly urls: string[];
  private urlIndex = 0;

  private pendingOutgoingMessage: string | undefined;

  private autoReconnectInterval = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  public room = "ets"; // no itx

  oneTimeCallbacks: Record<string, Array<(data: any) => void>> = {};
  private eventCallbacks: Record<string, Array<(data: any) => void>> = {};

  constructor(primaryUrl: string, fallbackUrl?: string, autoReconnect = true) {
    this.urls = fallbackUrl ? [primaryUrl, fallbackUrl] : [primaryUrl];
    this.autoReconnectInterval = autoReconnect ? 5000 : 0;
    this.connect();
  }

  removeCallback(callbackName: string, callback?: (data: any) => void) {
    try {
      if (!callback) {
        delete this.oneTimeCallbacks[callbackName];
        return;
      }

      const callbacks = this.oneTimeCallbacks[callbackName];
      if (!callbacks) return;
      this.oneTimeCallbacks[callbackName] = callbacks.filter((item) => item !== callback);
      if (this.oneTimeCallbacks[callbackName].length === 0) {
        delete this.oneTimeCallbacks[callbackName];
      }
    } catch (ex) {
      console.error("Error removing callback:", ex);
    }
  }

  on(eventName: string, callback: (data: any) => void) {
    if (!this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName] = [];
    }
    this.eventCallbacks[eventName].push(callback);
  }

  off(eventName: string, callback: (data: any) => void) {
    const callbacks = this.eventCallbacks[eventName];
    if (!callbacks) return;
    this.eventCallbacks[eventName] = callbacks.filter((item) => item !== callback);
  }

  connect() {
    this._disconnectSocket(); // internal: preserves autoReconnectInterval
    this.oneTimeCallbacks = {};

    const currentUrl = this.urls[this.urlIndex];
    let connectedOnThisAttempt = false;
    const socket = new WebSocket(currentUrl);
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      connectedOnThisAttempt = true;
      console.log("WebSocket connection established:", currentUrl);
      this.sendEvent("join", { userId: "estadaApp:" + state.loggedInUser?.name || "--" });

      //
      // Send any pending messages
      //
      if (this.pendingOutgoingMessage && this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(this.pendingOutgoingMessage);
        this.pendingOutgoingMessage = undefined;
      }
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      try {
        console.log("WebSocket message received:", event.data);
        const x = JSON.parse(event.data);
        const callbacks = [...(this.oneTimeCallbacks[x.eventName] || [])];
        callbacks.forEach((callback) => {
          try {
            callback(x.data);
          } catch (callbackError) {
            console.error("Error processing WebSocket one-time callback:", callbackError);
          }
        });
        const persistentCallbacks = this.eventCallbacks[x.eventName] || [];
        persistentCallbacks.forEach((persistentCallback) => {
          try {
            persistentCallback(x.data);
          } catch (callbackError) {
            console.error("Error processing WebSocket event callback:", callbackError);
          }
        });
      } catch (ex) {
        console.error("Error processing WebSocket message:", ex);
      }
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      if (!connectedOnThisAttempt && this.urls.length > 1) {
        // Connection never opened - advance to the next configured relay URL.
        this.urlIndex = (this.urlIndex + 1) % this.urls.length;
        console.warn(`WebSocket failed for ${currentUrl}, switching to ${this.urls[this.urlIndex]}`);
      }
      this.socket = null;
      console.log("WebSocket connection closed");

      if (this.autoReconnectInterval > 0) {
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = undefined;
          if (!this.isConnected) {
            this.connect();
          }
        }, this.autoReconnectInterval);
      }
    };
  }

  get isConnected(): boolean {
    return this.socket != null && this.socket.readyState === WebSocket.OPEN;
  }

  get isClosed(): boolean {
    return !this.socket || this.socket.readyState === WebSocket.CLOSED;
  }

  /**
   * Returns a Promise that resolves when the socket is open, or rejects after `timeoutMs`.
   * If already open, resolves immediately.
   */
  waitForOpen(timeoutMs = 2000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected) {
        resolve();
        return;
      }
      const socket = this.socket;
      // If socket is null or already CLOSED (between reconnect attempts), reject immediately
      // instead of waiting for the full timeout - event listeners on a CLOSED socket never fire.
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        reject(new Error("WebSocket is closed or unavailable")); // no itx
        return;
      }
      const timer = setTimeout(() => {
        socket.removeEventListener("open", onOpen);
        socket.removeEventListener("close", onClose);
        reject(new Error("WebSocket open timeout")); // no itx
      }, timeoutMs);
      const onOpen = () => {
        clearTimeout(timer);
        socket.removeEventListener("close", onClose);
        resolve();
      };
      const onClose = () => {
        clearTimeout(timer);
        socket.removeEventListener("open", onOpen);
        reject(new Error("WebSocket closed before open")); // no itx
      };
      socket.addEventListener("open", onOpen, { once: true });
      socket.addEventListener("close", onClose, { once: true });
    });
  }

  async sendCommandAsync(eventName: string, data: any, timeout: number = 5000, signal?: AbortSignal): Promise<any> {
    return new Promise((resolve, reject) => {
      if (isDevelopmentEnvironment() && timeout === 5000) {
        timeout = 10 * 60 * 1000; // increase timeout for dev environment: 10 minutes
      }
      const callBacknName = eventName + "Result"; // no itx
      let settled = false;
      let t: ReturnType<typeof setTimeout>;
      let commandCallback: ((resData: any) => void) | undefined;

      if (signal?.aborted) {
        resolve(undefined);
        return;
      }

      const finish = (resData?: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        signal?.removeEventListener("abort", onAbort);
        if (commandCallback) {
          this.removeCallback(callBacknName, commandCallback);
        } else {
          this.removeCallback(callBacknName);
        }
        resolve(resData);
      };

      const onAbort = () => finish(undefined);

      t = setTimeout(() => finish(undefined), timeout); // 1 second timeout
      signal?.addEventListener("abort", onAbort, { once: true });

      commandCallback = (resData: any) => {
        finish(resData);
      };
      this.sendEvent(eventName, data, callBacknName, commandCallback);
    });
  }

  sendEvent(eventName: string, data: any, callBacknName?: string, callback?: (resData: any) => void) {
    if (callBacknName && callback) {
      if (callBacknName === "pingResult" && this.oneTimeCallbacks[callBacknName]) {
        this.oneTimeCallbacks[callBacknName].push(callback);
      } else {
        this.oneTimeCallbacks[callBacknName] = [];
        this.oneTimeCallbacks[callBacknName].push(callback);
      }
    }

    if (
      this.getCurrentUrl() === LocalWsUrl &&
      selectedLocalEtsProjectId &&
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      !data.projectId
    ) {
      data = { ...data, projectId: selectedLocalEtsProjectId };
    }

    const x = { eventName, room: this.room, data };
    const message = JSON.stringify(x);
    this.sendRawMessage(message);
  }

  sendRawMessage(message: string) {
    if (this.isConnected && this.socket) {
      this.socket.send(message);
    } else {
      this.pendingOutgoingMessage = message;
      // this.connect();
    }
  }

  private _disconnectSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    if (this.socket) {
      this.socket.onclose = null; // prevent reconnect callback firing
      this.socket.close();
      this.socket = null;
    }
  }

  disconnect() {
    this.autoReconnectInterval = 0; // disable future auto-reconnects
    this._disconnectSocket();
  }

  /** Switch to the next URL in the list and reconnect. Returns true if a fallback exists. */
  tryFallbackUrl(): boolean {
    if (this.urls.length > 1) {
      this.urlIndex = (this.urlIndex + 1) % this.urls.length;
      this.connect();
      return true;
    }
    return false;
  }

  getCurrentUrl(): string {
    return this.urls[this.urlIndex];
  }
}

export let etsWebSocket: ETSWebSocket | undefined;

// ── Local ETS6 WebSocket (offline / same-machine mode) ──────────────────────
export const LocalWsUrl = "ws://localhost:1235/"; // no itx — local ETS6 server (no cloud relay needed)
export type EtsConnectionMode = "relay" | "local" | "offline"; // no itx
export let etsConnectionMode: EtsConnectionMode = "offline"; // no itx
export let localEtsWebSocket: ETSWebSocket | undefined;

export function setEtsConnectionMode(mode: EtsConnectionMode) {
  etsConnectionMode = mode;
}

/**
 * Returns the active WebSocket to use for ETS commands.
 * When the local ETS6 server is reachable, this returns the local socket;
 * otherwise it falls back to the cloud relay socket.
 */
export function getActiveEtsSocket(): ETSWebSocket | undefined {
  return etsConnectionMode === "local" ? localEtsWebSocket : etsWebSocket; // no itx
}

/**
 * Creates or reuses the local WebSocket probe to ws://localhost:1235/.
 * Local ETS is priority 3, so it does not auto-reconnect while relay checks are active.
 */
export function openLocalEtsWebSocket() {
  if (localEtsWebSocket && !localEtsWebSocket.isClosed) return;
  localEtsWebSocket?.disconnect();
  localEtsWebSocket = new ETSWebSocket(LocalWsUrl, undefined, false); // no itx, local is probed only as priority 3
}

export function closeLocalEtsWebSocket() {
  if (localEtsWebSocket) {
    localEtsWebSocket.disconnect();
    localEtsWebSocket = undefined;
  }
}
// ────────────────────────────────────────────────────────────────────────────

export function openEtsWebSocket(room: string) {
  if (!etsWebSocket) {
    // <--- prevent duplicates
    etsWebSocket = new ETSWebSocket(WssIonosUrl, WssAzureUrl);
    //   etsWebSocket = new ETSWebSocket("ws://localhost:4000");
  }
  etsWebSocket.room = room;
}

export function closeEtsWebSocket() {
  if (etsWebSocket) {
    etsWebSocket.oneTimeCallbacks = {};
    etsWebSocket.sendEvent("leave", { userId: "estadaApp:" + state.loggedInUser?.name || "--" });
    etsWebSocket.disconnect();
  }
  etsWebSocket = undefined;
}
